import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

let translations: { [key: string]: string } = {};

let locale = 'en-US';
let currency = 'USD';

export type DateFormatType =
    | 'DATECODE'
    | 'DATE_TIME'
    | 'SHORT_DATE_TIME'
    | 'TINY_DATE_TIME'
    | 'FULL_DATE_TIME'
    | 'DATE'
    | 'SHORT_DATE'
    | 'TINY_DATE'
    | 'FULL_DATE'
    | 'TEXT_DATE';

export function C(n: number, minDecimals?: number, maxDecimal?: number): string {
    return Number(n).toLocaleString(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: minDecimals ?? 0,
        maximumFractionDigits: maxDecimal ?? minDecimals ?? 0
    });
}

function getDateFormatOptions(
    dateLocale: string,
    options: {
        month?: 'numeric' | '2-digit';
        day?: 'numeric' | '2-digit';
        year?: 'numeric' | '2-digit';
        hour?: 'numeric' | '2-digit';
        minute?: '2-digit';
        hour12?: boolean;
    }
): Intl.DateTimeFormatOptions {
    const isDDMMYYYYLocale = dateLocale === 'en-AU' || dateLocale === 'en-NZ';

    return {
        ...options,
        month: isDDMMYYYYLocale && options.month === 'numeric' ? '2-digit' : options.month,
        day: isDDMMYYYYLocale && options.day === 'numeric' ? '2-digit' : options.day
    };
}

export function D(
    str: any,
    type?: DateFormatType,
    utcadjust?: boolean,
    dateLocale?: string
): string {
    dateLocale = normalizeLocale(dateLocale) || locale;
    let d = null;
    try {
        d = parseDateInput(str);
        if (utcadjust) {
            d = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        }
        switch (type) {
            case undefined:
            case 'DATECODE':
                return (
                    d.getFullYear().toString() +
                    digits(d.getMonth() + 1, 2) +
                    digits(d.getDate(), 2)
                );
            case 'DATE_TIME':
                return normalizeAmPm(
                    d
                        .toLocaleString(
                            dateLocale,
                            getDateFormatOptions(dateLocale, {
                                month: 'numeric',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                            })
                        )
                        .replace(',', '')
                        .toUpperCase()
                );
            case 'SHORT_DATE_TIME':
                return normalizeAmPm(
                    d
                        .toLocaleString(
                            dateLocale,
                            getDateFormatOptions(dateLocale, {
                                month: 'numeric',
                                day: 'numeric',
                                year: '2-digit',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                            })
                        )
                        .replace(',', '')
                        .toUpperCase()
                );
            case 'TINY_DATE_TIME':
                return d
                    .toLocaleString(
                        dateLocale,
                        getDateFormatOptions(dateLocale, {
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        })
                    )
                    .replace(' AM', 'am')
                    .replace(' PM', 'pm')
                    .replace(',', '');
            case 'FULL_DATE_TIME':
                return normalizeAmPm(
                    d
                        .toLocaleString(
                            dateLocale,
                            getDateFormatOptions(dateLocale, {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            })
                        )
                        .replace(',', '')
                        .toUpperCase()
                );
            case 'DATE':
                return d.toLocaleString(
                    dateLocale,
                    getDateFormatOptions(dateLocale, {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                    })
                );
            case 'SHORT_DATE':
                return d.toLocaleString(
                    dateLocale,
                    getDateFormatOptions(dateLocale, {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit'
                    })
                );
            case 'TINY_DATE':
                return d.toLocaleString(
                    dateLocale,
                    getDateFormatOptions(dateLocale, { month: 'numeric', day: 'numeric' })
                );
            case 'FULL_DATE':
                return d.toLocaleString(
                    dateLocale,
                    getDateFormatOptions(dateLocale, {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                    })
                );
        }
    } catch (e) {}
    return str;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * ★ UX-934. A bare `YYYY-MM-DD` is a calendar day, not an instant, so it becomes LOCAL midnight of
 * that day. `new Date('2026-10-07')` would read it as UTC midnight (ECMAScript date-only form),
 * which is the 6th anywhere west of UTC. Everything else, including zone-less date-times and
 * `Date` objects, goes through `new Date(...)` unchanged.
 */
function parseDateInput(str: any): Date {
    const match = typeof str === 'string' ? DATE_ONLY_PATTERN.exec(str) : null;
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    return new Date(str);
}

function digits(n: number, digits: number) {
    return String(n).padStart(digits, '0');
}

/**
 * Converts a country code to a locale string for date formatting.
 * @param countryCode - Country code (e.g., 'CA', 'US', 'AU', 'NZ') or locale string (e.g., 'en-CA')
 * @returns Locale string (e.g., 'en-CA', 'en-US')
 */
function normalizeLocale(countryCode?: string): string {
    if (!countryCode) return locale;

    // If it's already a locale string (contains '-'), return as is
    if (countryCode.includes('-')) {
        return countryCode;
    }

    // Convert country code to locale string
    const countryToLocale: Record<string, string> = {
        US: 'en-US',
        CA: 'en-CA',
        AU: 'en-AU',
        NZ: 'en-NZ',
        Other: 'en-US'
    };

    return countryToLocale[countryCode] || locale;
}

/**
 * Cleans up AM/PM formatting by removing periods and normalizing to uppercase AM/PM.
 * Handles cases like "A. M." -> "AM", "P. M." -> "PM", "A.M." -> "AM", etc.
 */
function normalizeAmPm(str: string): string {
    return (
        str
            // Remove periods from AM/PM in various formats: "A. M." -> "AM", "A.M." -> "AM", "P. M." -> "PM"
            .replace(/\b([AaPp])\.?\s*([Mm])\.?\b/g, '$1$2')
            // Normalize spacing around AM/PM
            .replace(/\s+([AaPp][Mm])\s*/g, ' $1')
            .trim()
    );
}

/** Locale-aware time (hour + minute, 12h) for a full date, consistent with {@link D}. */
export function formatLocaleTime(
    str: string | Date | null | undefined,
    dateLocale?: string
): string {
    if (str == null || str === '') return '';
    const dl = normalizeLocale(dateLocale) || locale;
    try {
        const d = new Date(str);
        if (Number.isNaN(d.getTime())) return '';
        return normalizeAmPm(
            d.toLocaleTimeString(dl, {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
        );
    } catch {
        return '';
    }
}

/**
 * Minutes from local midnight (e.g. team schedule / shoot times) as a locale-aware 12h time.
 * Same locale rules as {@link D}; not for full dates — use `D` for those.
 */
export function formatMinuteOfDay(minutesFromMidnight: number, dateLocale?: string): string {
    const dl = normalizeLocale(dateLocale) || locale;
    const m = Math.max(0, Math.min(minutesFromMidnight, 24 * 60 - 1));
    const d = new Date(2000, 0, 1, Math.floor(m / 60), m % 60, 0, 0);
    return normalizeAmPm(
        d.toLocaleTimeString(dl, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })
    );
}

export function getLocale(): string {
    return locale || 'en-US';
}

export function getRegionName(): string {
    switch (locale) {
        case 'en-US':
            return 'State';
        case 'en-CA':
            return 'Province';
        case 'en-AU':
            return 'State';
        case 'en-NZ':
            return 'Region';
    }
    return 'State';
}

export function local2utc(str: string): Date {
    const d = new Date(str);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000);
}

export function N(n: number | string, minDecimals?: number, maxDecimal?: number): string {
    return Number(n).toLocaleString(locale, {
        minimumFractionDigits: minDecimals ?? 0,
        maximumFractionDigits: maxDecimal ?? minDecimals ?? 0
    });
}

export function setCurrency(c: string) {
    currency = c;
}

/** Active ISO 4217 currency code (e.g. for API or Intl). */
export function getCurrency(): string {
    return currency || 'USD';
}

/** Narrow currency symbol for the active locale and currency (e.g. $, €). */
export function getCurrencySymbol(): string {
    try {
        const parts = new Intl.NumberFormat(locale || 'en-US', {
            style: 'currency',
            currency: currency || 'USD',
            currencyDisplay: 'narrowSymbol'
        }).formatToParts(0);
        return parts.find(p => p.type === 'currency')?.value ?? '$';
    } catch {
        return '$';
    }
}

export function setLocale(l: string) {
    locale = l;
}

export function setTranslations(trans: { [key: string]: any }) {
    Object.keys(trans).forEach(o => {
        if (typeof trans[o] === 'object') {
            if (trans[o]['default']) {
                trans[o][''] = trans[o]['default'];
                delete trans[o]['default'];
            }
        }
    });
    translations = trans;
}

export function T(str: string): string {
    const path = document.location.pathname;
    const trans = translations[str];
    if (trans) {
        if (typeof trans === 'object') {
            const keys = Object.keys(trans).sort().reverse();
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                if (path.startsWith(key)) {
                    return trans[key];
                }
            }
        }
    }
    return str;
}

export function utc2local(str: string): Date {
    const d = new Date(str);
    return new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000);
}

/**
 * Converts a server date to the brand's wall clock, using the host's hour offset from UTC.
 *
 * The result is a `Date` whose LOCAL fields (`getHours()`, `D(...)`, `formatLocaleTime(...)`)
 * read as brand time, whatever the browser's own zone is.
 *
 * ★ UX-786. The input is read as UTC either way: a zoneless string (`2026-09-28T22:24:34`, the
 * wall-clock UTC most payloads carry) is taken as UTC, and a string with an explicit zone
 * (`...+00:00`, `...Z`, as `orders/{id}` `createDate` and `order-views/search` `created` now
 * send) or a `Date` is taken as the instant it names. This used to be `dayjs(date).add(offset)`,
 * which parsed a zoneless string as BROWSER-local (fine — its fields are the UTC fields) but
 * converted a zoned one into the browser's zone first, so the offset was applied twice: a 3:27 PM
 * PDT order showed as 8:27 AM for a -7 brand viewed from a Pacific browser.
 *
 * @param date - The date to adjust (string or Date)
 * @param timezoneOffset - The brand's offset from UTC in hours (e.g., 8 for UTC+8, -5 for UTC-5)
 * @returns A new Date whose local fields are the brand's wall-clock time
 */
export function adjustDateForTimezone(
    date: string | Date | null | undefined,
    timezoneOffset: number
): Date {
    if (!date) {
        throw new Error('Date is required');
    }
    const brand = dayjs.utc(date).add(timezoneOffset, 'hour');
    return new Date(
        brand.year(),
        brand.month(),
        brand.date(),
        brand.hour(),
        brand.minute(),
        brand.second(),
        brand.millisecond()
    );
}

/**
 * The brand's date-entry format, by its locale (`brandLocale`: US, CA, AU, NZ, Other).
 *
 * ★ ONLY THE US IS MONTH-FIRST (UX-760 QA, Herb 2026-10-19). The ACs give Canada, New Zealand, Australia
 *   and Other DD/MM/YYYY. Canada and Other used to map to MM/DD/YYYY here. An unknown value (none
 *   reaches here once the brand loads: `getCountry` maps a missing country to US) stays MM/DD/YYYY.
 */
export const getCountryDateFormat = (countryCode: string) => {
    const countryToDateFormat: Record<string, string> = {
        US: 'MM/DD/YYYY',
        CA: 'DD/MM/YYYY',
        AU: 'DD/MM/YYYY',
        NZ: 'DD/MM/YYYY',
        Other: 'DD/MM/YYYY'
    };

    return countryToDateFormat[countryCode] || 'MM/DD/YYYY';
};
